import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getToken } from "@/lib/consult/authSession";
import { api as consultApi } from "@/lib/consult/api";
import { Check, ChevronDown, ChevronRight, ClipboardPaste, Trash2, X } from "lucide-react";
import { parsePatientPaste, splitName } from "./patientPaste";

// Admin side of the medication locker program (Outreach → Locker tab).
// Sites are managed like pharmacy partners (no licence/OCP), each site has 18
// compartments, clients carry a required ID photo + total weeks, archiving a
// client frees the compartment and preserves a read-only history record, and
// partner dispenses land here as $10 service-fee payment requests.

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const lapi = axios.create({ baseURL: `${BACKEND}/api/locker` });
lapi.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers["X-Auth-Token"] = t;
  return config;
});

const NAVY = "#1F3A5F";
const GREEN = "#2E7D5B";
const BORDER = "#E3E0D9";
const MUTED = "#6B7280";
const TEXT = "#1F2937";
const RED = "#B4351B";

const card = { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: 16 };
const input = { width: "100%", height: 38, padding: "0 10px", fontSize: 13, border: `1px solid ${BORDER}`, borderRadius: 8, boxSizing: "border-box", fontFamily: "inherit" };
const label = { display: "block", fontSize: 11, fontWeight: 700, color: MUTED, marginBottom: 4, paddingLeft: 11, textTransform: "uppercase", letterSpacing: 0.4 };
const btn = (bg, fg = "#fff") => ({ background: bg, color: fg, border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" });
const btnGhost = { background: "#fff", color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };

const EMPTY_SITE = { name: "", street: "", city: "", province: "ON", postal: "", contact_name: "", contact_phone: "", contact_email: "", notes: "", enabled: true };
const EMPTY_CLIENT = { first_name: "", last_name: "", dob: "", total_tablets: 84, total_cash: "", medication: "", location: "", client_status: "Active", staff_id: "", compartment: "", photo_data_url: "", phone: "", notes: "", notes_additional: "", consultation: "no" };

// The admin locker grid is hidden — the Patients list opens the same client
// popup partners/staff get (plus admin extras). Flip to true to restore it.
const SHOW_LOCKER_GRID = false;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const money0 = (n) => `$${Math.round(Number(n) || 0).toLocaleString("en-CA")}`; // round, thousands separators — matches the partner portal

// Same phone format used across the app: (416) 555-0100
function fmtPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return "";
}

// Auto-growing textarea — always shows at least `minRows` lines and expands
// with its content, so full notes are readable without in-box scrolling.
function AutoArea({ value, style, minRows = 4, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(minRows * 20 + 16, el.scrollHeight)}px`;
  }, [value, minRows]);
  return <textarea ref={ref} rows={minRows} value={value} style={{ ...style, overflow: "hidden", resize: "none", lineHeight: "20px" }} {...props} />;
}

// Canadian postal mask: A1A 1A1 (auto-space, correct char type per position)
function fmtPostal(v) {
  const raw = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  let out = "";
  for (const ch of raw) {
    const i = out.length;
    if (i >= 6) break;
    if (i % 2 === 0 ? !/[A-Z]/.test(ch) : !/[0-9]/.test(ch)) continue;
    out += ch;
  }
  return out.length > 3 ? `${out.slice(0, 3)} ${out.slice(3)}` : out;
}
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—";

function resizePhoto(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 480;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });
}

export default function LockerPanel() {
  const [sites, setSites] = useState(null);
  const [lockerCount, setLockerCount] = useState(18);
  const [openSite, setOpenSite] = useState(null);   // { id, name, ... }
  const [board, setBoard] = useState(null);
  const [queue, setQueue] = useState([]);
  const [paid, setPaid] = useState([]);
  const [cashQueue, setCashQueue] = useState([]);
  const [cashPaid, setCashPaid] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const [payFilter, setPayFilter] = useState("active"); // overview toggle: active | inactive sites
  const [payOpen, setPayOpen] = useState(null);         // site id with payments expanded
  const [cashDlg, setCashDlg] = useState(null);         // site object with the cash float dialog open
  const [payDetail, setPayDetail] = useState(null);     // { siteId, clientId, name, profile } — per-client patients popup
  const [payPhoto, setPayPhoto] = useState(false);
  const [medications, setMedications] = useState([]);   // shared consult_medications list (same as consultations)
  const [medsOpen, setMedsOpen] = useState(false);
  const [locations, setLocations] = useState([]);       // medication locations (locker-managed list)
  const [locsOpen, setLocsOpen] = useState(false);
  const [statuses, setStatuses] = useState([]);         // client program statuses (locker-managed list)
  const [statusesOpen, setStatusesOpen] = useState(false);
  const [boardStatusFilter, setBoardStatusFilter] = useState(""); // "" = all statuses on the board

  // Staff adherence payments ($50/week per client, max 12 weeks).
  const [batches, setBatches] = useState([]);              // unapproved per-staff batches
  const [issued, setIssued] = useState([]);                // recent issued payments (undo only)
  const [issuedOpen, setIssuedOpen] = useState(false);
  const [siteStaff, setSiteStaff] = useState([]);          // staff of the open site (board + client form)
  const [cashTransfer, setCashTransfer] = useState(null);  // pending reassignment cash-handover confirmation
  const [staffOpen, setStaffOpen] = useState(null);        // site id with staff section expanded
  const [staffBySite, setStaffBySite] = useState({});      // overview: site id → staff list
  const [newStaffName, setNewStaffName] = useState("");
  const [payable, setPayable] = useState(null);            // payable weeks for the payDetail client

  // Client statuses (Active / Pending / Inactive by default).
  const loadStatuses = useCallback(async () => {
    try {
      const { data } = await lapi.get("/statuses");
      setStatuses(data?.statuses || []);
    } catch { /* keep the list we have */ }
  }, []);
  useEffect(() => { loadStatuses(); }, [loadStatuses]);

  // Medication locations (Shelter / Outreach Team / Pharmacy by default).
  const loadLocations = useCallback(async () => {
    try {
      const { data } = await lapi.get("/locations");
      setLocations(data?.locations || []);
    } catch { /* keep the list we have */ }
  }, []);
  useEffect(() => { loadLocations(); }, [loadLocations]);

  // Same medication list the consultations module uses (add/rename/delete shared).
  const loadMeds = useCallback(async () => {
    try {
      const { data } = await consultApi.get("/medications");
      setMedications(data?.items || []);
    } catch { /* keep the list we have */ }
  }, []);
  useEffect(() => { loadMeds(); }, [loadMeds]);

  const [siteForm, setSiteForm] = useState(null);   // null | {mode:'add'|'edit', data, id?}
  const [clientForm, setClientForm] = useState(null); // null | {mode, data, id?, error}
  const [confirmRegen, setConfirmRegen] = useState(null); // site
  const [confirmDeleteSite, setConfirmDeleteSite] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null); // {client, n}
  const [holdForm, setHoldForm] = useState(null);   // { client, reason }
  const [history, setHistory] = useState(null);     // { site, rows }
  const [historyDetail, setHistoryDetail] = useState(null); // { client, entries }
  const [historyPhoto, setHistoryPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [s, q, p, cq, cp] = await Promise.all([
      lapi.get("/admin/sites"),
      lapi.get("/admin/pickups", { params: { status: "submitted" } }),
      lapi.get("/admin/pickups", { params: { status: "paid" } }),
      lapi.get("/admin/cash", { params: { status: "submitted" } }),
      lapi.get("/admin/cash", { params: { status: "paid" } }),
    ]);
    setSites(s.data.sites);
    setLockerCount(s.data.locker_count);
    setQueue(q.data.pickups);
    setPaid(p.data.pickups);
    setCashQueue(cq.data.cash);
    setCashPaid(cp.data.cash);
  }, []);

  const loadBatches = useCallback(async () => {
    try {
      const { data } = await lapi.get("/admin/staff-batch");
      setBatches(data?.batches || []);
    } catch { /* keep what we have */ }
  }, []);

  const loadIssued = useCallback(async () => {
    try {
      const { data } = await lapi.get("/admin/staff-payments");
      setIssued((data?.payments || []).slice(0, 8));
    } catch { /* keep what we have */ }
  }, []);

  useEffect(() => { loadBatches(); loadIssued(); }, [loadBatches, loadIssued]);

  useEffect(() => { load().catch((e) => toast.error(e?.response?.data?.detail || "Load failed")); }, [load]);

  const openBoard = async (site) => {
    setOpenSite(site);
    const { data } = await lapi.get(`/admin/sites/${site.id}/board`);
    setBoard(data.board);
    try {
      const st = await lapi.get("/admin/staff", { params: { site_id: site.id } });
      setSiteStaff(st.data?.staff || []);
    } catch { setSiteStaff([]); }
  };

  const refreshBoard = async () => {
    if (!openSite) return;
    const { data } = await lapi.get(`/admin/sites/${openSite.id}/board`);
    setBoard(data.board);
    load();
  };

  const photoOf = (clientId) => `${BACKEND}/api/locker/photo/${clientId}.png?token=${getToken()}`;

  // City filter options come only from cities actually entered on sites.
  const cityOptions = useMemo(
    () => [...new Set((sites || []).map((x) => (x.city || "").trim()).filter(Boolean))].sort(),
    [sites]
  );
  const visibleSites = useMemo(
    () => (sites || [])
      .filter((x) => (cityFilter ? (x.city || "").trim() === cityFilter : true))
      .filter((x) => (payFilter === "active" ? x.enabled !== false : x.enabled === false)),
    [sites, cityFilter, payFilter]
  );

  // ---------- site CRUD ----------
  const saveSite = async () => {
    setSaving(true);
    try {
      if (siteForm.mode === "add") {
        const { data } = await lapi.post("/admin/sites", siteForm.data);
        toast.success(`Site added — code ${data.code}`);
      } else {
        await lapi.patch(`/admin/sites/${siteForm.id}`, siteForm.data);
        toast.success("Site updated");
      }
      setSiteForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    setSaving(true);
    try {
      const { data } = await lapi.post(`/admin/sites/${confirmRegen.id}/regenerate-code`);
      toast.success(`New code: ${data.code} — the old code is locked out`);
      setConfirmRegen(null);
      await load();
      if (openSite?.id === confirmRegen?.id) setOpenSite(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const deleteSite = async () => {
    setSaving(true);
    try {
      await lapi.delete(`/admin/sites/${confirmDeleteSite.id}`);
      toast.success("Site removed (history retained)");
      setConfirmDeleteSite(null); setOpenSite(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  // ---------- client CRUD ----------
  const freeCompartments = useMemo(() => {
    if (!board) return [];
    const taken = new Set(board.filter((c) => c.occupied).map((c) => c.n));
    const free = [];
    for (let n = 1; n <= lockerCount; n++) if (!taken.has(n)) free.push(n);
    return free;
  }, [board, lockerCount]);

  const pickPhoto = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await resizePhoto(file);
      setClientForm((f) => ({ ...f, data: { ...f.data, photo_data_url: dataUrl }, error: "" }));
    } catch {
      setClientForm((f) => ({ ...f, error: "Could not read that image — try another photo" }));
    }
  };

  const saveClient = async (cashConfirmedArg = false) => {
    // onSave is wired to a button onClick — the first arg may be the click
    // event, so only an explicit `true` counts as confirmed.
    const cashConfirmed = cashConfirmedArg === true;
    const d = clientForm.data;
    if (!d.first_name.trim() || !d.last_name.trim()) return setClientForm((f) => ({ ...f, error: "First and last name are required" }));
    if (!d.dob) return setClientForm((f) => ({ ...f, error: "Date of birth is required" }));
    // Locker number is optional — a site can have more clients than lockers.
    setSaving(true);
    try {
      if (clientForm.mode === "add") {
        await lapi.post(`/admin/sites/${openSite.id}/clients`, { ...d, total_tablets: Number(d.total_tablets) || 84, total_cash: Number(d.total_cash) || 0, compartment: Number(d.compartment) || null, staff_id: d.staff_id || "" });
        toast.success(d.compartment ? `${d.first_name} assigned to locker ${d.compartment}` : `${d.first_name} added`);
      } else {
        // Reassigning adherence staff with client cash still outstanding needs
        // an explicit transfer-and-verified confirmation before saving.
        const staffChanged = (d.staff_id || "") !== (d.__orig_staff_id || "");
        if (staffChanged && !cashConfirmed) {
          const { data: prof } = await lapi.get(`/admin/clients/${clientForm.id}`);
          const remaining = Math.round(((Number(prof.total_cash) || 0) - (Number(prof.cash_given) || 0)) * 100) / 100;
          if (remaining > 0) {
            const nameOf = (sid) => (siteStaff.find((s) => s.id === sid)?.name) || "the site float";
            setCashTransfer({
              remaining,
              fromName: nameOf(d.__orig_staff_id || ""),
              toName: nameOf(d.staff_id || ""),
              checked: false,
            });
            setSaving(false);
            return;
          }
        }
        const body = { first_name: d.first_name, last_name: d.last_name, dob: d.dob, total_tablets: Number(d.total_tablets), total_cash: Number(d.total_cash) || 0, medication: d.medication || "", location: d.location || "", client_status: d.client_status || "Active", staff_id: d.staff_id || "", phone: d.phone || "", notes: d.notes || "", notes_additional: d.notes_additional || "", consultation: d.consultation || "no", compartment: Number(d.compartment) };
        if (staffChanged) body.cash_transfer_confirmed = true;
        if (d.photo_data_url && d.photo_data_url.startsWith("data:")) body.photo_data_url = d.photo_data_url;
        await lapi.patch(`/admin/clients/${clientForm.id}`, body);
        toast.success("Client updated");
      }
      setCashTransfer(null);
      setClientForm(null);
      await refreshBoard();
    } catch (e) {
      // Backend-side guard: reassignment raced with new cash activity.
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det && det.code === "cash_transfer_confirmation_required") {
        setCashTransfer({ remaining: det.remaining, fromName: det.from_staff, toName: det.to_staff, checked: false });
      } else {
        setClientForm((f) => ({ ...f, error: (typeof det === "string" ? det : null) || "Save failed" }));
      }
    } finally { setSaving(false); }
  };

  const archiveClient = async () => {
    setSaving(true);
    try {
      const { data } = await lapi.delete(`/admin/clients/${confirmArchive.client.id}`);
      toast.success(`${confirmArchive.client.first_name} archived — ${data.tablets_completed} tablet(s) kept in history, locker ${confirmArchive.n} is free`);
      setConfirmArchive(null);
      await refreshBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const holdClient = async (clientId, reason) => {
    setSaving(true);
    try {
      await lapi.post(`/admin/clients/${clientId}/hold`, { reason });
      toast.success("Access restricted — the site will see 'call HepCURE' instead of the dispense button");
      setHoldForm(null);
      setClientForm(null);
      await refreshBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const unholdClient = async (clientId, name) => {
    setSaving(true);
    try {
      await lapi.post(`/admin/clients/${clientId}/unhold`);
      toast.success(`Access restored for ${name} — the site can dispense again`);
      setClientForm(null);
      await refreshBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const openHistory = async () => {
    const { data } = await lapi.get(`/admin/sites/${openSite.id}/history`);
    setHistory({ site: openSite, rows: data.history });
    setHistoryDetail(null);
  };

  const openHistoryDetail = async (h) => {
    const { data } = await lapi.get(`/admin/clients/${h.id}/entries`);
    setHistoryPhoto(false);
    setHistoryDetail({ client: h, entries: data.entries, cashEntries: data.cash_entries || [] });
  };

  // Patients section: open one client's full read-only profile + every entry.
  const openPayDetail = async (siteId, g) => {
    const { data } = await lapi.get(`/admin/clients/${g.client_id}`);
    setPayPhoto(false);
    setPayDetail({ siteId, clientId: g.client_id, name: g.name, profile: data });
    try {
      const pw = await lapi.get(`/admin/clients/${g.client_id}/payable`);
      setPayable(pw.data);
    } catch { setPayable(null); }
  };

  // ---------- staff adherence payments ----------
  const addWeekToBatch = async (clientId, week) => {
    try {
      await lapi.post("/admin/staff-batch/items", { client_id: clientId, week });
      toast.success("Added to the payment batch");
      const pw = await lapi.get(`/admin/clients/${clientId}/payable`);
      setPayable(pw.data);
      loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that week");
    }
  };

  const removeBatchItem = async (staffId, clientId, week) => {
    try {
      await lapi.delete(`/admin/staff-batch/items/${staffId}/${clientId}/${week}`);
      loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove that row");
    }
  };

  const approveBatch = async (b) => {
    if (!window.confirm(`Approve the ${money0(b.total)} payment to ${b.staff_name} (${b.items.length} client week${b.items.length === 1 ? "" : "s"})? It shows on their portal right away for confirmation.`)) return;
    setSaving(true);
    try {
      await lapi.post(`/admin/staff-batch/${b.staff_id}/approve`, { date: new Date().toLocaleDateString("en-CA") });
      toast.success(`Payment of ${money0(b.total)} issued to ${b.staff_name}`);
      loadBatches();
      loadIssued();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve");
    } finally { setSaving(false); }
  };

  // Undo a whole DAY of issued payments — every payment from that date returns
  // its weeks to the staff batches so missed items can be added and the day
  // resubmitted as one bulk round.
  const undoPaymentDay = async (day, ps, siteId) => {
    const names = [...new Set(ps.map((p) => p.staff_name))].join(", ");
    const total = ps.reduce((a, p) => a + (p.amount || 0), 0);
    const wks = ps.reduce((a, p) => a + (p.items || []).length, 0);
    if (!window.confirm(`Undo all ${ps.length} payment${ps.length === 1 ? "" : "s"} issued on ${fmtD(day)} (${names} — ${money0(total)}, ${wks} wk${wks === 1 ? "" : "s"})?\n\nEvery week from that day goes back into the batches above — add anything missed and approve once to resubmit the day.`)) return;
    try {
      const { data } = await lapi.delete(`/admin/staff-payments/day/${day}`, { params: siteId ? { site_id: siteId } : {} });
      toast.success(`Undone — ${data.restored} week${data.restored === 1 ? "" : "s"} back in the batch${data.restored === 1 ? "" : "es"}`);
      loadIssued();
      loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not undo");
    }
  };

  const toggleStaff = async (s) => {
    if (staffOpen === s.id) { setStaffOpen(null); return; }
    setStaffOpen(s.id);
    setNewStaffName("");
    try {
      const { data } = await lapi.get("/admin/staff", { params: { site_id: s.id } });
      setStaffBySite((m) => ({ ...m, [s.id]: data?.staff || [] }));
    } catch { /* keep */ }
  };

  const addStaff = async (s) => {
    const name = newStaffName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const { data } = await lapi.post(`/admin/sites/${s.id}/staff`, { name });
      toast.success(`${data.name} added — code ${data.code}`);
      setNewStaffName("");
      const { data: fresh } = await lapi.get("/admin/staff", { params: { site_id: s.id } });
      setStaffBySite((m) => ({ ...m, [s.id]: fresh?.staff || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add staff");
    } finally { setSaving(false); }
  };

  const revokeStaff = async (s, member) => {
    if (!window.confirm(`Revoke ${member.name}'s access code (${member.code})? They are locked out immediately; their payment history is kept.`)) return;
    try {
      await lapi.patch(`/admin/staff/${member.id}`, { active: false });
      toast.success(`${member.name} revoked`);
      const { data: fresh } = await lapi.get("/admin/staff", { params: { site_id: s.id } });
      setStaffBySite((m) => ({ ...m, [s.id]: fresh?.staff || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    }
  };

  // Delete is for mistakes — only allowed while the staff member has no
  // clients assigned (enforced server-side too).
  const deleteStaff = async (s, member) => {
    if (!window.confirm(`Delete ${member.name}? Their code stops working immediately and this can't be undone.`)) return;
    try {
      await lapi.delete(`/admin/staff/${member.id}`);
      toast.success(`${member.name} deleted`);
      const { data: fresh } = await lapi.get("/admin/staff", { params: { site_id: s.id } });
      setStaffBySite((m) => ({ ...m, [s.id]: fresh?.staff || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  // An entry can be removed only while unprocessed — after that it's locked.
  const undoPickup = async (p) => {
    const isCash = p.kind === "cash";
    const what = isCash ? `${money(p.amount)} cash` : `${p.tablets} tablets`;
    if (!window.confirm(`Remove the ${fmtD(p.service_date)} entry for ${p.client_name} (${what})? This can't be undone.`)) return;
    try {
      await lapi.delete(isCash ? `/admin/cash/${p.id}` : `/admin/pickups/${p.id}`);
      toast.success(`Entry removed — ${p.client_name}, ${fmtD(p.service_date)}`);
      if (openSite) await refreshBoard(); else await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // ---------- per-client entry grouping + the shared patient popup ----------
  // (defined before the board/overview returns so BOTH can render it)
  const allFor = (siteId) => [
    ...queue.filter((p) => p.site_id === siteId).map((p) => ({ ...p, kind: "med" })),
    ...paid.filter((p) => p.site_id === siteId).map((p) => ({ ...p, kind: "med" })),
    ...cashQueue.filter((p) => p.site_id === siteId).map((p) => ({ ...p, kind: "cash" })),
    ...cashPaid.filter((p) => p.site_id === siteId).map((p) => ({ ...p, kind: "cash" })),
  ].sort((a, b) => String(b.submitted_at).localeCompare(String(a.submitted_at)));
  // Group a site's entries per client: name, tablets, cash, rows.
  const clientTotals = (siteId) => {
    const m = new Map();
    for (const p of allFor(siteId)) {
      const g = m.get(p.client_id) || { client_id: p.client_id, name: p.client_name, tablets: 0, cash: 0, rows: [] };
      g.rows.push(p);
      if (p.kind === "cash") g.cash += Number(p.amount) || 0;
      else g.tablets += Number(p.tablets) || 0;
      m.set(p.client_id, g);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  };
  const payDetailGroup = payDetail
    ? clientTotals(payDetail.siteId).find((g) => g.client_id === payDetail.clientId)
      || { client_id: payDetail.clientId, name: payDetail.name, tablets: 0, cash: 0, rows: [] }
    : null;
  const payDetailModal = payDetailGroup && payDetail ? (
    <Modal title={payDetail.name} onClose={() => { setPayDetail(null); setPayPhoto(false); }} testId="locker-paydetail">
      {openSite && payDetail.profile && (
        <div style={{ textAlign: "right", marginBottom: 4 }}>
          <button data-testid="locker-paydetail-edit" style={{ ...btnGhost, padding: "5px 12px", fontSize: 12 }}
            onClick={() => {
              const p = payDetail.profile;
              setPayDetail(null); setPayPhoto(false);
              setClientForm({ mode: "edit", id: payDetail.clientId, data: { first_name: p.first_name, last_name: p.last_name, dob: p.dob, total_tablets: p.total_tablets, total_cash: p.total_cash || "", medication: p.medication || "", location: p.location || "", client_status: p.client_status || "Active", staff_id: p.staff_id || "", __orig_staff_id: p.staff_id || "", phone: p.phone || "", notes: p.notes || "", notes_additional: p.notes_additional || "", consultation: p.consultation || "no", has_photo: Boolean(p.photo_url), compartment: p.compartment, photo_data_url: "" }, error: "" });
            }}>Edit client</button>
        </div>
      )}
      {payDetail.profile && (
        <div style={{ textAlign: "center" }}>
          {payDetail.profile.photo_url ? (
            <img src={photoOf(payDetail.clientId)} alt="Client ID — tap to view full screen"
              data-testid="locker-paydetail-photo"
              onClick={() => setPayPhoto(true)}
              style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: `3px solid ${NAVY}`, background: "#D8D5CE", margin: "0 auto 10px", display: "block", cursor: "zoom-in" }} />
          ) : (
            <div data-testid="locker-paydetail-no-photo"
              style={{ width: 96, height: 96, borderRadius: "50%", background: "#D8D5CE", border: `3px solid ${NAVY}`, margin: "0 auto 10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: MUTED }}>
              {payDetail.profile.first_name?.[0]}{payDetail.profile.last_name?.[0]}
            </div>
          )}
          <div style={{ fontSize: 13, color: MUTED }} data-testid="locker-paydetail-info">
            DOB {payDetail.profile.dob}{payDetail.profile.compartment ? ` · Locker ${payDetail.profile.compartment}` : ""}
          </div>
          {payDetail.profile.medication && (
            <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }} data-testid="locker-paydetail-med">
              Medication: {payDetail.profile.medication}
            </div>
          )}
          <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginTop: 6 }} data-testid="locker-paydetail-progress">
            {payDetail.profile.tablets_done} of {payDetail.profile.total_tablets} tablets given
            {payDetail.profile.tablets_done >= payDetail.profile.total_tablets ? " · treatment complete" : ""}
          </div>
          {payDetail.profile.total_cash > 0 && (
            <div style={{ fontSize: 12, fontWeight: 700, color: GREEN, marginTop: 3 }} data-testid="locker-paydetail-cash-progress">
              {money(payDetail.profile.cash_given)} of {money(payDetail.profile.total_cash)} cash given
              {payDetail.profile.cash_given >= payDetail.profile.total_cash ? " · fully paid" : ""}
            </div>
          )}
          {payDetail.profile.status === "archived" && (
            <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Archived {fmtD(payDetail.profile.archived_at)}</div>
          )}
        </div>
      )}
      <div style={{ fontSize: 12, color: MUTED, margin: "10px 0 6px" }} data-testid="locker-paydetail-total">
        {payDetailGroup.tablets} tablets{payDetailGroup.cash > 0 ? ` + ${money(payDetailGroup.cash)} cash` : ""} across {payDetailGroup.rows.length} entr{payDetailGroup.rows.length === 1 ? "y" : "ies"}
      </div>
      {[...payDetailGroup.rows].sort((a, b) => String(a.service_date).localeCompare(String(b.service_date))).map((p) => (
        <div key={p.id} data-testid={`locker-paydetail-entry-${p.id}`}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>{fmtD(p.service_date)}</b>{p.compartment ? <span style={{ whiteSpace: "nowrap" }}> · Locker {p.compartment}</span> : null}
            <div style={{ fontSize: 10, color: MUTED }}>
              Recorded {fmtDT(p.submitted_at)}
            </div>
          </div>
          {p.kind === "cash" ? (
            <span data-testid={`locker-paydetail-cash-${p.id}`}
              style={{ fontSize: 10, fontWeight: 800, color: GREEN, border: `1.5px solid ${GREEN}`, borderRadius: 99, padding: "2px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>{money(p.amount)}</span>
          ) : (
            <span data-testid={`locker-paydetail-tabs-${p.id}`}
              style={{ fontSize: 10, fontWeight: 800, color: NAVY, border: `1.5px solid ${NAVY}`, borderRadius: 99, padding: "2px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>{p.tablets} tabs</span>
          )}
          {p.status === "submitted" && (
            <button data-testid={`locker-undo-${p.id}`} style={{ ...btnGhost, padding: "5px 9px", fontSize: 11, color: RED, borderColor: "#E9BFB1" }} onClick={() => undoPickup(p)}>Undo</button>
          )}
        </div>
      ))}
      {payable && (
        <div data-testid="locker-paydetail-staffpay" style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", background: "#FAF9F6", marginTop: 12, textAlign: "left" }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, marginBottom: 6 }}>
            Staff payment{payable.staff_name ? ` — ${payable.staff_name}` : ""}
          </div>
          {!payable.staff_id ? (
            <div style={{ fontSize: 12, color: MUTED }} data-testid="locker-paydetail-no-staff">Assign an adherence staff member (Edit client) to enable $50/week payments.</div>
          ) : payable.weeks.length === 0 ? (
            <div style={{ fontSize: 12, color: MUTED }} data-testid="locker-paydetail-no-weeks">No dose entries yet — payable weeks appear here as entries are recorded.</div>
          ) : (
            payable.weeks.map((w) => (
              <div key={w.week} data-testid={`locker-payweek-${w.week}`}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
                <span style={{ flex: 1, fontWeight: 600 }}>Week {w.n} · {fmtD(w.week)}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: NAVY }}>{money0(w.amount)}</span>
                {w.state === "paid" ? (
                  <span data-testid={`locker-payweek-paid-${w.week}`} style={{ fontSize: 10, fontWeight: 800, color: GREEN, background: "#E7F2EC", borderRadius: 99, padding: "3px 10px" }}>PAID</span>
                ) : w.state === "added" ? (
                  <span data-testid={`locker-payweek-added-${w.week}`} style={{ fontSize: 10, fontWeight: 800, color: GREEN, background: "#E7F2EC", borderRadius: 99, padding: "3px 10px" }}>ADDED ✓</span>
                ) : (
                  <button data-testid={`locker-payweek-add-${w.week}`}
                    style={{ background: "#fff", color: NAVY, border: `1.5px solid ${NAVY}`, borderRadius: 99, padding: "4px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                    onClick={() => addWeekToBatch(payDetail.clientId, w.week)}>+ Add</button>
                )}
              </div>
            ))
          )}
          <div style={{ fontSize: 10.5, color: MUTED, marginTop: 8 }}>Each week with a recorded dose can be added once — max $50 per week, 12 weeks per client.</div>
        </div>
      )}
      {payPhoto && (
        <div data-testid="locker-paydetail-photo-fullscreen" onClick={() => setPayPhoto(false)}
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", minHeight: "100vh", background: "rgba(0,0,0,.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box", zIndex: 80 }}>
          <button data-testid="locker-paydetail-photo-close" onClick={() => setPayPhoto(false)} aria-label="Close photo"
            style={{ position: "absolute", top: 14, right: 14, width: 42, height: 42, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.16)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X style={{ width: 20, height: 20 }} strokeWidth={2.5} />
          </button>
          <img src={photoOf(payDetail.clientId)} alt="Client ID — full view" data-testid="locker-paydetail-photo-img"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }} />
        </div>
      )}
    </Modal>
  ) : null;

  // ---------- payment batch cards (both views; board scopes to its site) ----------
  const batchCards = (siteId) => {
    const rows = siteId ? batches.filter((b) => b.site_id === siteId) : batches;
    if (!rows.length) return null;
    return rows.map((b) => (
      <div key={b.staff_id} data-testid={`locker-batch-${b.staff_id}`} style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, flex: 1 }}>
            Payment — {b.staff_name}{b.site_name ? ` · ${b.site_name}` : ""}
          </span>
          <span data-testid={`locker-batch-total-${b.staff_id}`} style={{ fontSize: 14, fontWeight: 800, color: NAVY }}>{money0(b.total)}</span>
        </div>
        {b.items.map((it) => (
          <div key={`${it.client_id}-${it.week}`} data-testid={`locker-batch-item-${it.client_id}-${it.week}`}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
            <span style={{ flex: 1, fontWeight: 600 }}>{it.client_name} <span style={{ color: MUTED, fontWeight: 500 }}>· week of {fmtD(it.week)}</span></span>
            <span style={{ fontSize: 13, fontWeight: 800 }}>{money0(it.amount)}</span>
            <button data-testid={`locker-batch-del-${it.client_id}-${it.week}`}
              style={{ background: "none", border: "none", color: RED, fontSize: 14, cursor: "pointer", padding: "0 2px" }}
              onClick={() => removeBatchItem(b.staff_id, it.client_id, it.week)}>×</button>
          </div>
        ))}
        <button data-testid={`locker-batch-approve-${b.staff_id}`} disabled={saving}
          style={{ ...btn(GREEN), width: "100%", height: 42, marginTop: 10, fontSize: 14, opacity: saving ? 0.7 : 1 }}
          onClick={() => approveBatch(b)}>Approve payment · {money0(b.total)}</button>
      </div>
    ));
  };

  // ---------- issued staff payments: minimized undo strip (both views) ----------
  // Grouped by day: one Undo per day pulls every payment from that date back
  // into the staff batches so the day can be corrected and resubmitted bulk.
  const issuedStrip = (siteId) => {
    // Overview: follows the city/site filters; board view: scoped to the site.
    const visibleIds = siteId ? null : new Set(visibleSites.map((s) => s.id));
    const rows = siteId
      ? issued.filter((p) => p.site_id === siteId)
      : issued.filter((p) => visibleIds.has(p.site_id));
    if (!rows.length) return null;
    const byDate = {};
    for (const p of rows) (byDate[p.date] = byDate[p.date] || []).push(p);
    const dates = Object.keys(byDate).sort().reverse();
    return (
      <div data-testid={siteId ? "locker-issued-payments-board" : "locker-issued-payments"} style={{ ...card, padding: "12px 16px", marginBottom: 14 }}>
        <button type="button" data-testid={siteId ? "locker-issued-toggle-board" : "locker-issued-toggle"}
          onClick={() => setIssuedOpen(!issuedOpen)}
          style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, textAlign: "left" }}>
            Issued payments ({dates.length})
          </span>
          {issuedOpen ? <ChevronDown style={{ width: 13, height: 13, color: MUTED, flexShrink: 0 }} /> : <ChevronRight style={{ width: 13, height: 13, color: MUTED, flexShrink: 0 }} />}
          <span style={{ flex: 1 }} />
        </button>
        {issuedOpen && (
          <div style={{ marginTop: 4 }}>
            {dates.map((d) => {
              const ps = byDate[d];
              const total = ps.reduce((a, p) => a + (p.amount || 0), 0);
              const wks = ps.reduce((a, p) => a + (p.items || []).length, 0);
              const allConfirmed = ps.every((p) => p.status === "confirmed");
              return (
                <div key={d} data-testid={`locker-issued-day-${d}`}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <b>{fmtD(d)}</b> — {[...new Set(ps.map((p) => p.staff_name))].join(", ")} <span style={{ color: MUTED }}>({wks} wk{wks === 1 ? "" : "s"})</span>
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: GREEN, whiteSpace: "nowrap" }}>{money0(total)}</span>
                  {allConfirmed && (
                    <span style={{ fontSize: 10, fontWeight: 800, color: GREEN, background: "#E7F2EC", borderRadius: 99, padding: "3px 10px", whiteSpace: "nowrap" }}>Confirmed</span>
                  )}
                  <button data-testid={`locker-issued-undo-${d}`}
                    style={{ ...btnGhost, padding: "4px 10px", fontSize: 11, color: RED, borderColor: "#E9BFB1", whiteSpace: "nowrap" }}
                    onClick={() => undoPaymentDay(d, ps, siteId)}>Undo</button>
                </div>
              );
            })}
            <div style={{ fontSize: 10.5, color: MUTED, marginTop: 6, lineHeight: 1.45 }}>
              Undo pulls every payment from that day back into the batches above — add anything missed, then approve once to resubmit the day.
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================== RENDER ==============================
  if (!sites) return <div style={{ padding: 30, color: MUTED, fontSize: 13 }}>Loading locker program…</div>;

  // ---------- site detail (board) ----------
  if (openSite) {
    return (
      <div data-testid="locker-site-view" style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", alignItems: "center" }}>
            <button data-testid="locker-back" style={{ ...btnGhost, flex: "0 0 auto", padding: "7px 9px", fontSize: 12, whiteSpace: "nowrap" }} onClick={() => { setOpenSite(null); setBoard(null); load(); }}>← All sites</button>
            <button data-testid="locker-history-btn" style={{ ...btnGhost, flex: "0 0 auto", padding: "7px 9px", fontSize: 12, whiteSpace: "nowrap" }} onClick={openHistory}>History</button>
            <button data-testid="locker-edit-site-btn" style={{ ...btnGhost, flex: "0 0 auto", padding: "7px 9px", fontSize: 12, whiteSpace: "nowrap" }}
              onClick={() => setSiteForm({ mode: "edit", id: openSite.id, data: { ...EMPTY_SITE, ...openSite } })}>Edit site</button>
            <select data-testid="locker-status-filter" value={boardStatusFilter}
              onChange={(e) => setBoardStatusFilter(e.target.value)}
              style={{ ...btnGhost, flex: 1, minWidth: 0, fontSize: 12, padding: "7px 6px", textOverflow: "ellipsis" }}>
              <option value="">Disposition</option>
              {(statuses || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: NAVY }}>{openSite.name}</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
              {openSite.street && <div>{openSite.street}</div>}
              <div>{[openSite.city, openSite.province, openSite.postal].filter(Boolean).join(", ")}</div>
              {(openSite.contact_name || openSite.contact_phone) && (
                <div>{[openSite.contact_name, openSite.contact_phone].filter(Boolean).join(" ")}</div>
              )}
            </div>
          </div>
        </div>

        {SHOW_LOCKER_GRID && (
        <div style={{ ...card, marginBottom: 14, padding: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, background: "#C4C9CE", border: "1px solid #A9AEB4", borderRadius: 10, padding: 8, boxShadow: "0 2px 8px rgba(0,0,0,.15)" }}>
            {(board || []).map((cell) => {
              const c = cell.client;
              return (
                <button
                  key={cell.n}
                  data-testid={`locker-admin-cell-${cell.n}`}
                  onClick={() => {
                    if (cell.occupied) {
                      setClientForm({ mode: "edit", id: c.id, data: { first_name: c.first_name, last_name: c.last_name, dob: c.dob, total_tablets: c.total_tablets, total_cash: c.total_cash || "", medication: c.medication || "", location: c.location || "", client_status: c.client_status || "Active", staff_id: c.staff_id || "", __orig_staff_id: c.staff_id || "", phone: c.phone || "", notes: c.notes || "", notes_additional: c.notes_additional || "", consultation: c.consultation || "no", has_photo: Boolean(c.has_photo), compartment: cell.n, photo_data_url: "" }, error: "" });
                    } else {
                      setClientForm({ mode: "add", data: { ...EMPTY_CLIENT, compartment: cell.n }, error: "" });
                    }
                  }}
                  style={{
                    position: "relative", aspectRatio: "3/4.3", minHeight: 150, borderRadius: 5,
                    border: "1px solid #AEB4BA",
                    background: "linear-gradient(180deg,#EDEFF1 0%,#D9DDE1 55%,#CBD0D4 100%)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,.8), 0 1px 2px rgba(0,0,0,.10)",
                    cursor: "pointer", fontFamily: "inherit", padding: 0, overflow: "hidden",
                    opacity: boardStatusFilter && cell.occupied && (c?.client_status || "Active") !== boardStatusFilter ? 0.3 : 1,
                  }}
                >
                  {/* louvered vents + keypad — steel locker door */}
                  <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", width: "52%", height: 14, background: "repeating-linear-gradient(180deg,#8F959B 0 2px,transparent 2px 5px)" }} />
                  <div style={{ position: "absolute", right: 6, top: "40%", width: 11, height: 22, background: "#2B2F33", borderRadius: 2, boxShadow: "inset 0 1px 2px rgba(0,0,0,.6)" }}>
                    <div style={{ width: 5, height: 3, margin: "3px auto 0", borderRadius: 1, background: cell.occupied ? (c.hold ? "#F1948A" : c.complete ? "#7EE787" : "#FFD166") : "#555" }} />
                  </div>
                  <span style={{ position: "absolute", top: 26, left: "50%", transform: "translateX(-50%)", fontSize: 10, fontWeight: 800, color: "#5B6167" }}>{cell.n}</span>
                  {cell.occupied ? (
                    <div style={{
                      position: "absolute", left: 5, right: 5, bottom: 6, borderRadius: 4,
                      background: c.hold ? "#FBEAE5" : c.complete ? "#E7F2EC" : "#fff",
                      border: `1px solid ${c.hold ? RED : c.complete ? GREEN : BORDER}`,
                      padding: "4px 3px", display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                    }}>
                      <span data-testid={`locker-cell-status-${cell.n}`}
                        style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", lineHeight: 1, color: (c.client_status || "Active") === "Active" ? GREEN : (c.client_status || "Active") === "Pending" ? "#B7791F" : MUTED }}>
                        {c.client_status || "Active"}
                      </span>
                      <span style={{ fontSize: 9, fontWeight: 800, textAlign: "center", lineHeight: 1.15, color: TEXT }}>{c.first_name}<br />{c.last_name}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, color: c.hold ? RED : c.complete ? GREEN : MUTED }}>
                        {c.hold ? "RESTRICTED" : c.complete ? "COMPLETE" : `${c.tablets_done}/${c.total_tablets} tabs`}
                      </span>
                    </div>
                  ) : (
                    <div style={{ position: "absolute", left: 5, right: 5, bottom: 6, borderRadius: 4, background: "#F4F5F6", border: `1px dashed ${BORDER}`, padding: "6px 3px", fontSize: 10, fontWeight: 700, color: MUTED, textAlign: "center" }}>
                      + Assign client
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* patients — the same minimized client list partners/staff see; tap
            opens the same client popup, with admin extras (Edit, staff payment
            block, Undo). The locker grid above is hidden (SHOW_LOCKER_GRID). */}
        <div data-testid="locker-admin-patients" style={{ ...card, marginBottom: 14, padding: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>Patients</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: MUTED }} data-testid="locker-admin-patients-count">
              {(board || []).filter((cell) => cell.occupied && (!boardStatusFilter || (cell.client?.client_status || "Active") === boardStatusFilter)).length}
            </span>
            <span style={{ flex: 1 }} />
            <button data-testid="locker-admin-add-client" style={{ ...btn(GREEN), padding: "6px 12px", fontSize: 12 }}
              onClick={() => setClientForm({ mode: "add", data: { ...EMPTY_CLIENT, compartment: freeCompartments[0] || "" }, error: "" })}>+ Add client</button>
          </div>
          {(board || []).filter((cell) => cell.occupied && (!boardStatusFilter || (cell.client?.client_status || "Active") === boardStatusFilter)).map((cell) => {
            const c = cell.client;
            return (
              <button key={c.id} type="button" data-testid={`locker-admin-patient-${c.id}`}
                onClick={() => openPayDetail(openSite.id, { client_id: c.id, name: `${c.first_name} ${c.last_name}` })}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", background: "none", border: "none", borderTop: `1px solid ${BORDER}`, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 13, color: TEXT }}>{c.first_name} {c.last_name}</span>
                <span style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", color: (c.client_status || "Active") === "Active" ? GREEN : (c.client_status || "Active") === "Pending" ? "#B7791F" : MUTED }}>{c.client_status || "Active"}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, whiteSpace: "nowrap" }}>
                  {c.tablets_done}/{c.total_tablets} tabs{c.total_cash > 0 ? ` · ${money0(c.cash_given)}/${money0(c.total_cash)}` : ""}
                </span>
                <ChevronRight style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} strokeWidth={2.5} />
              </button>
            );
          })}
          {(board || []).filter((cell) => cell.occupied).length === 0 && (
            <div style={{ fontSize: 12, color: MUTED, padding: "0 14px 12px" }} data-testid="locker-admin-patients-empty">No clients yet — add one.</div>
          )}
        </div>

        {/* payment batches + issued payments for THIS site's staff — build,
            approve, undo and resubmit without leaving the site view */}
        {batchCards(openSite.id)}
        {issuedStrip(openSite.id)}

        {/* history modal */}
        {history && (
          <Modal onClose={() => setHistory(null)} testId="locker-history-modal" title={`History — ${history.site.name}`}>
            {history.rows.length === 0 ? (
              <div style={{ fontSize: 13, color: MUTED }}>No archived clients yet.</div>
            ) : history.rows.map((h) => (
              <button key={h.id} type="button" data-testid={`locker-history-row-${h.id}`}
                onClick={() => openHistoryDetail(h)}
                style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 2px", width: "100%", background: "none", border: "none", borderBottom: `1px solid ${BORDER}`, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                {h.photo_url ? (
                  <img src={photoOf(h.id)} alt="" style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", background: "#D8D5CE" }} />
                ) : (
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#D8D5CE", color: MUTED, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                    {h.first_name?.[0]}{h.last_name?.[0]}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{h.first_name} {h.last_name}</div>
                  <div style={{ fontSize: 11, color: MUTED }}>
                    DOB {h.dob}{h.compartment ? ` · Locker ${h.compartment}` : ""} · {h.tablets_completed ?? h.pickups * 7}/{h.total_tablets} tablets · {h.pickups} entr{h.pickups === 1 ? "y" : "ies"} · archived {fmtD(h.archived_at)}
                  </div>
                </div>
                <ChevronRight style={{ width: 14, height: 14, color: MUTED, flexShrink: 0 }} strokeWidth={2.5} />
              </button>
            ))}
          </Modal>
        )}

        {/* history detail — full read-only profile of one archived client */}
        {history && historyDetail && (
          <Modal onClose={() => { setHistoryDetail(null); setHistoryPhoto(false); }} testId="locker-history-detail" title={`${historyDetail.client.first_name} ${historyDetail.client.last_name}`}>
            <div style={{ textAlign: "center" }}>
              {historyDetail.client.photo_url ? (
                <img src={photoOf(historyDetail.client.id)} alt="Client ID — tap to view full screen"
                  data-testid="locker-history-detail-photo"
                  onClick={() => setHistoryPhoto(true)}
                  style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: `3px solid ${NAVY}`, background: "#D8D5CE", margin: "0 auto 10px", display: "block", cursor: "zoom-in" }} />
              ) : (
                <div data-testid="locker-history-detail-no-photo"
                  style={{ width: 96, height: 96, borderRadius: "50%", background: "#D8D5CE", border: `3px solid ${NAVY}`, margin: "0 auto 10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: MUTED }}>
                  {historyDetail.client.first_name?.[0]}{historyDetail.client.last_name?.[0]}
                </div>
              )}
              <div style={{ fontSize: 13, color: MUTED }} data-testid="locker-history-detail-total">
                DOB {historyDetail.client.dob}{historyDetail.client.compartment ? ` · Locker ${historyDetail.client.compartment}` : ""}
              </div>
              {historyDetail.client.medication && (
                <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }} data-testid="locker-history-detail-med">
                  Medication: {historyDetail.client.medication}
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginTop: 6 }} data-testid="locker-history-detail-progress">
                {historyDetail.client.tablets_completed ?? 0} of {historyDetail.client.total_tablets} tablets given
                {(historyDetail.client.tablets_completed ?? 0) >= historyDetail.client.total_tablets ? " · treatment complete" : ""}
              </div>
              {(historyDetail.client.total_cash > 0) && (
                <div style={{ fontSize: 12, fontWeight: 700, color: GREEN, marginTop: 3 }} data-testid="locker-history-detail-cash-progress">
                  {money(historyDetail.client.cash_completed ?? 0)} of {money(historyDetail.client.total_cash)} cash given
                </div>
              )}
              <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Archived {fmtD(historyDetail.client.archived_at)}</div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ ...label, paddingLeft: 0 }}>Dispenses</label>
              {historyDetail.entries.length === 0 ? (
                <div style={{ fontSize: 12, color: MUTED }} data-testid="locker-history-detail-empty">No entries were recorded.</div>
              ) : historyDetail.entries.map((p) => (
                <div key={p.id} data-testid={`locker-history-entry-${p.id}`}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
                  <div style={{ flex: 1, minWidth: 0, color: TEXT }}>
                    <b>{fmtD(p.service_date)}</b>{p.compartment ? <span style={{ whiteSpace: "nowrap" }}> · Locker {p.compartment}</span> : null}
                    <div style={{ fontSize: 10, color: MUTED }}>
                      Recorded {fmtDT(p.submitted_at)}
                    </div>
                  </div>
                  <span data-testid={`locker-history-entry-tabs-${p.id}`}
                    style={{ fontSize: 10, fontWeight: 800, color: NAVY, border: `1.5px solid ${NAVY}`, borderRadius: 99, padding: "2px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>{p.tablets} tabs</span>
                </div>
              ))}
            </div>
            {(historyDetail.cashEntries || []).length > 0 && (
              <div style={{ marginTop: 12 }} data-testid="locker-history-detail-cash">
                <label style={{ ...label, paddingLeft: 0 }}>Cash payments</label>
                {historyDetail.cashEntries.map((p) => (
                  <div key={p.id} data-testid={`locker-history-cash-entry-${p.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
                    <div style={{ flex: 1, minWidth: 0, color: TEXT }}>
                      <b>{fmtD(p.service_date)}</b>{p.compartment ? <span style={{ whiteSpace: "nowrap" }}> · Locker {p.compartment}</span> : null}
                      <div style={{ fontSize: 10, color: MUTED }}>
                        Recorded {fmtDT(p.submitted_at)}
                      </div>
                    </div>
                    <span data-testid={`locker-history-cash-amount-${p.id}`}
                      style={{ fontSize: 10, fontWeight: 800, color: GREEN, border: `1.5px solid ${GREEN}`, borderRadius: 99, padding: "2px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {historyPhoto && (
              <div data-testid="locker-history-photo-fullscreen" onClick={() => setHistoryPhoto(false)}
                style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", minHeight: "100vh", background: "rgba(0,0,0,.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box", zIndex: 80 }}>
                <button data-testid="locker-history-photo-close" onClick={() => setHistoryPhoto(false)} aria-label="Close photo"
                  style={{ position: "absolute", top: 14, right: 14, width: 42, height: 42, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.16)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X style={{ width: 20, height: 20 }} strokeWidth={2.5} />
                </button>
                <img src={photoOf(historyDetail.client.id)} alt="Client ID — full view" data-testid="locker-history-photo-img"
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }} />
              </div>
            )}
          </Modal>
        )}

        {payDetailModal}

        {clientForm && (
          <ClientModal
            form={clientForm} setForm={setClientForm} saving={saving}
            freeCompartments={freeCompartments} board={board}
            fileRef={fileRef} pickPhoto={pickPhoto} onSave={saveClient}
            onArchive={(c, n) => { setClientForm(null); setConfirmArchive({ client: c, n }); }}
            onRestrict={(c) => setHoldForm({ client: c, reason: "" })}
            onRestore={(c) => unholdClient(c.id, `${c.first_name} ${c.last_name}`)}
            photoOf={photoOf}
            weeks={[...queue, ...paid].filter((p) => p.client_id === clientForm.id).sort((a, b) => String(a.service_date).localeCompare(String(b.service_date)))}
            cashWeeks={[...cashQueue, ...cashPaid].filter((p) => p.client_id === clientForm.id).sort((a, b) => String(a.service_date).localeCompare(String(b.service_date)))}
            onUndo={undoPickup}
            medications={medications} onManageMeds={() => setMedsOpen(true)}
            locations={locations} onManageLocations={() => setLocsOpen(true)}
            statuses={statuses} onManageStatuses={() => setStatusesOpen(true)}
            staffList={siteStaff}
          />
        )}

        {medsOpen && <MedsModal medications={medications} onChanged={loadMeds} onClose={() => setMedsOpen(false)} />}
        {locsOpen && <LocationsModal sites={sites} currentSiteId={openSite?.id || ""} locations={locations} onChanged={loadLocations} onClose={() => setLocsOpen(false)} />}
        {statusesOpen && <StatusesModal statuses={statuses} onChanged={loadStatuses} onClose={() => setStatusesOpen(false)} />}
        {cashTransfer && (
          <Modal title="Confirm cash transfer" onClose={() => setCashTransfer(null)} testId="locker-cash-transfer-confirm">
            <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.55, marginBottom: 12 }}>
              This client still has <b data-testid="locker-cash-transfer-amount">{money0(cashTransfer.remaining)}</b> of
              qualified cash remaining, currently under <b>{cashTransfer.fromName}</b>.
            </div>
            <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.55, marginBottom: 12, background: "rgba(229,138,46,0.10)", border: "1px solid rgba(229,138,46,0.35)", borderRadius: 8, padding: "10px 12px" }}>
              Before this reassignment is saved, confirm that the remaining cash has been physically
              transferred and verified to <b>{cashTransfer.toName}</b>. This confirmation is logged so
              no cash is unaccounted for.
            </div>
            <label data-testid="locker-cash-transfer-check-label" style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: TEXT, cursor: "pointer", marginBottom: 14 }}>
              <input
                type="checkbox"
                data-testid="locker-cash-transfer-check"
                checked={cashTransfer.checked}
                onChange={(e) => setCashTransfer((t) => ({ ...t, checked: e.target.checked }))}
                style={{ marginTop: 2 }}
              />
              <span>I confirm the remaining cash of <b>{money0(cashTransfer.remaining)}</b> has been transferred and verified to <b>{cashTransfer.toName}</b>.</span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                data-testid="locker-cash-transfer-confirm-btn"
                disabled={!cashTransfer.checked || saving}
                onClick={() => saveClient(true)}
                style={{ ...btn(GREEN), flex: 1, opacity: (!cashTransfer.checked || saving) ? 0.5 : 1 }}
              >
                Confirm &amp; save
              </button>
              <button
                type="button"
                data-testid="locker-cash-transfer-cancel"
                onClick={() => setCashTransfer(null)}
                style={{ ...btnGhost, flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
        {holdForm && (
          <Modal title={`Restrict ${holdForm.client.first_name} ${holdForm.client.last_name}?`} onClose={() => setHoldForm(null)} testId="locker-hold-confirm">
            <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.5, marginBottom: 10 }}>
              The partner site will see <b>"Do not give medication — the client must call HepCURE at 1-844-925-CURE (2873)"</b> instead of the dispense button, until you restore access.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
              <button data-testid="locker-hold-cancel" style={btnGhost} onClick={() => setHoldForm(null)}>Cancel</button>
              <button data-testid="locker-hold-ok" style={{ ...btn(RED), opacity: saving ? 0.7 : 1 }}
                onClick={() => holdClient(holdForm.client.id, holdForm.reason)} disabled={saving}>
                {saving ? "Working…" : "Revoke"}
              </button>
            </div>
          </Modal>
        )}
        {confirmArchive && (
          <Confirm
            testId="locker-archive-confirm"
            title={`Remove ${confirmArchive.client.first_name} ${confirmArchive.client.last_name}?`}
            body={`Their record (photo, DOB, ${confirmArchive.client.tablets_done} of ${confirmArchive.client.total_tablets} tablets, all entries) moves to the history, and locker ${confirmArchive.n} becomes available for a new client. This cannot be edited after.`}
            confirmLabel="Yes, remove client"
            saving={saving}
            onConfirm={archiveClient}
            onClose={() => setConfirmArchive(null)}
          />
        )}
        {siteForm && <SiteModal form={siteForm} setForm={setSiteForm} saving={saving} onSave={saveSite} />}
      </div>
    );
  }

  // ---------- sites overview ----------
  return (
    <div data-testid="locker-panel" style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* sites */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <select
          data-testid="locker-city-filter"
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          style={{ ...input, flex: "1 1 180px", minWidth: 180, height: 34, fontSize: 12 }}
        >
          <option value="">All cities</option>
          {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          data-testid="locker-payfilter-toggle"
          style={{ ...btnGhost, height: 34, minWidth: 104, textAlign: "center", whiteSpace: "nowrap", fontWeight: 800, color: payFilter === "active" ? GREEN : RED, borderColor: payFilter === "active" ? "#BFDCC9" : "#E9BFB1" }}
          onClick={() => setPayFilter(payFilter === "active" ? "inactive" : "active")}
        >
          {payFilter === "active"
            ? `Active (${(sites || []).filter((x) => x.enabled !== false).length})`
            : `Inactive (${(sites || []).filter((x) => x.enabled === false).length})`}
        </button>
        <button data-testid="locker-add-site" style={{ ...btn(NAVY), height: 34, padding: "0 12px", fontSize: 12, whiteSpace: "nowrap" }}
          onClick={() => setSiteForm({ mode: "add", data: { ...EMPTY_SITE } })}>+ Add site</button>
      </div>

      {/* staff payment batches — weeks added from client profiles pile up
          here until one tap approves the whole per-staff payment */}
      {batchCards()}

      {/* issued payments — minimized undo strip for mistakes; undoing returns
          the weeks to the batch so the payment can be revised and resubmitted */}
      {issuedStrip()}

      {sites.length === 0 && (
        <div style={{ fontSize: 13, color: MUTED }} data-testid="locker-empty">
          No sites have been added yet.
        </div>
      )}
      {sites.length > 0 && visibleSites.length === 0 && (
        <div style={{ fontSize: 13, color: MUTED }} data-testid="locker-empty-filtered">
          {payFilter === "active" ? "No active sites." : "No inactive sites."}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
        {visibleSites.map((s) => (
          <div key={s.id} style={card} data-testid={`locker-site-card-${s.id}`}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: TEXT }}>{s.name}</div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                  {/* street line, then city/province/postal line */}
                  {s.street && <div>{s.street}</div>}
                  {[s.city, s.province, s.postal].filter(Boolean).length > 0 && (
                    <div>{[s.city, s.province, s.postal].filter(Boolean).join(", ")}</div>
                  )}
                  {!s.street && [s.city, s.province, s.postal].filter(Boolean).length === 0 && "No address"}
                </div>
                {s.contact_name && <div style={{ fontSize: 11, color: MUTED }}>{s.contact_name}{s.contact_phone ? ` ${s.contact_phone}` : ""}</div>}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0" }}>
              <code data-testid={`locker-code-${s.id}`} style={{ background: "#F1EFE9", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "3px 8px", fontSize: 13, fontWeight: 800, letterSpacing: 1, color: NAVY }}>{s.code}</code>
              <button data-testid={`locker-copy-${s.id}`} style={btnGhost}
                onClick={() => { navigator.clipboard?.writeText(s.code); toast.success("Code copied"); }}>Copy</button>
              <span style={{ fontSize: 11, color: MUTED, marginLeft: "auto" }}>{s.occupied}/{lockerCount} lockers</span>
              {s.enabled === false && <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, border: `1.5px solid ${MUTED}`, borderRadius: 99, padding: "2px 8px" }}>Inactive</span>}
            </div>
            {/* cash float — top-ups minus cash given to clients, per site */}
            <button type="button" data-testid={`locker-cash-toggle-${s.id}`}
              onClick={() => setCashDlg(s)}
              style={{ ...btnGhost, width: "100%", display: "flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 12 }}>
              Cash on hand
              <span data-testid={`locker-cash-remaining-${s.id}`} style={{ marginLeft: "auto", fontWeight: 800, color: (s.cash_remaining ?? 0) < 0 ? RED : GREEN }}>{money0(s.cash_remaining ?? 0)}</span>
            </button>
            {/* payments toggle — expands this card to show the site's payment rows */}
            <button type="button" data-testid={`locker-payments-toggle-${s.id}`}
              onClick={() => setPayOpen(payOpen === s.id ? null : s.id)}
              style={{ ...btnGhost, width: "100%", display: "flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 12 }}>
              {payOpen === s.id ? <ChevronDown style={{ width: 13, height: 13 }} strokeWidth={2.5} /> : <ChevronRight style={{ width: 13, height: 13 }} strokeWidth={2.5} />}
              Patients
            </button>
            {payOpen === s.id && (
              <div data-testid={`locker-payments-${s.id}`} style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 10px", marginBottom: 10, background: "#FAF9F6" }}>
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 2 }}>Tap a client to see each entry</div>
                {clientTotals(s.id).length === 0 ? (
                  <div style={{ fontSize: 12, color: MUTED, padding: "4px 0" }}>No weeks dispensed yet.</div>
                ) : clientTotals(s.id).map((g) => (
                  <button key={g.client_id} type="button" data-testid={`locker-paygroup-${g.client_id}`}
                    onClick={() => openPayDetail(s.id, g)}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 2px", background: "none", border: "none", borderTop: `1px solid ${BORDER}`, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    <span style={{ flex: 1, minWidth: 0, textAlign: "left", fontWeight: 700, color: TEXT }}>{g.name}</span>
                    <span style={{ fontWeight: 800, color: NAVY, whiteSpace: "nowrap" }}>{g.tablets} tabs · {money0(g.cash)}</span>
                    <ChevronRight style={{ width: 13, height: 13, color: MUTED, flexShrink: 0 }} strokeWidth={2.5} />
                  </button>
                ))}
              </div>
            )}
            {/* staff — adherence staff with personal portal codes ($50/wk per client) */}
            <button type="button" data-testid={`locker-staff-toggle-${s.id}`}
              onClick={() => toggleStaff(s)}
              style={{ ...btnGhost, width: "100%", display: "flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 12 }}>
              {staffOpen === s.id ? <ChevronDown style={{ width: 13, height: 13 }} strokeWidth={2.5} /> : <ChevronRight style={{ width: 13, height: 13 }} strokeWidth={2.5} />}
              Staff
            </button>
            {staffOpen === s.id && (
              <div data-testid={`locker-staff-${s.id}`} style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 10px", marginBottom: 10, background: "#FAF9F6" }}>
                {(staffBySite[s.id] || []).length === 0 && (
                  <div style={{ fontSize: 12, color: MUTED, padding: "2px 0 6px" }} data-testid={`locker-staff-empty-${s.id}`}>No staff yet — add the person managing adherence at this site.</div>
                )}
                {(staffBySite[s.id] || []).map((m) => (
                  <div key={m.id} data-testid={`locker-staff-row-${m.id}`}
                    style={{ padding: "8px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: m.active === false ? MUTED : TEXT }}>{m.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "nowrap" }}>
                      <code data-testid={`locker-staff-code-${m.id}`} style={{ background: "#F1EFE9", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "2px 7px", fontSize: 11, fontWeight: 800, letterSpacing: 1, color: NAVY, whiteSpace: "nowrap", flexShrink: 0 }}>{m.code}</code>
                      <span style={{ flex: 1 }} />
                      <button data-testid={`locker-staff-copy-${m.id}`} style={{ ...btnGhost, padding: "4px 8px", fontSize: 11 }}
                        onClick={() => { navigator.clipboard?.writeText(m.code); toast.success("Code copied"); }}>Copy</button>
                      {m.active === false ? (
                        <span style={{ fontSize: 10, fontWeight: 800, color: MUTED, letterSpacing: ".06em" }}>REVOKED</span>
                      ) : (
                        <button data-testid={`locker-staff-revoke-${m.id}`} style={{ ...btnGhost, padding: "4px 8px", fontSize: 11, color: RED, borderColor: "#E9BFB1" }}
                          onClick={() => revokeStaff(s, m)}>Revoke</button>
                      )}
                      <button data-testid={`locker-staff-delete-${m.id}`}
                        disabled={(m.client_count || 0) > 0}
                        title={(m.client_count || 0) > 0 ? "Reassign their clients first" : "Delete this staff member"}
                        style={{ ...btnGhost, padding: "4px 8px", fontSize: 11, color: RED, borderColor: "#E9BFB1", whiteSpace: "nowrap", opacity: (m.client_count || 0) > 0 ? 0.4 : 1, cursor: (m.client_count || 0) > 0 ? "not-allowed" : "pointer" }}
                        onClick={() => deleteStaff(s, m)}>Delete</button>
                    </div>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <input data-testid={`locker-staff-name-${s.id}`} style={{ ...input, flex: 1, minWidth: 0, height: 34, fontSize: 12 }}
                    placeholder="Staff name" value={newStaffName} onChange={(e) => setNewStaffName(e.target.value)} />
                  <button data-testid={`locker-staff-add-${s.id}`} disabled={saving || !newStaffName.trim()}
                    style={{ ...btn(GREEN), height: 34, padding: "0 12px", fontSize: 12, opacity: saving || !newStaffName.trim() ? 0.6 : 1 }}
                    onClick={() => addStaff(s)}>Add</button>
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
              <button data-testid={`locker-open-${s.id}`}
                style={{ ...btn(NAVY), flex: 0.75, minWidth: 0, whiteSpace: "nowrap", padding: "8px 4px", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}
                onClick={() => openBoard(s)}>View</button>
              <button data-testid={`locker-edit-card-${s.id}`}
                style={{ ...btnGhost, flex: 0.75, minWidth: 0, whiteSpace: "nowrap", padding: "7px 4px", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}
                onClick={() => setSiteForm({ mode: "edit", id: s.id, data: { ...EMPTY_SITE, ...s } })}>Edit</button>
              <button data-testid={`locker-regen-${s.id}`}
                style={{ ...btnGhost, flex: 1.3, minWidth: 0, whiteSpace: "nowrap", padding: "7px 4px", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}
                onClick={() => setConfirmRegen(s)}>New Code</button>
              <button data-testid={`locker-del-site-${s.id}`}
                style={{ ...btnGhost, flex: 1, minWidth: 0, whiteSpace: "nowrap", padding: "7px 4px", fontSize: 12, color: RED, borderColor: "#E9BFB1", overflow: "hidden", textOverflow: "ellipsis" }}
                onClick={() => setConfirmDeleteSite(s)}>Remove</button>
            </div>
          </div>
        ))}
      </div>

      {payDetailModal}

      {siteForm && <SiteModal form={siteForm} setForm={setSiteForm} saving={saving} onSave={saveSite} />}
      {cashDlg && <SiteCashDialog site={cashDlg} onClose={() => setCashDlg(null)} onChanged={load} />}
      {confirmRegen && (
        <Confirm
          testId="locker-regen-confirm"
          title={`Issue a new code for ${confirmRegen.name}?`}
          body="The current code stops working immediately — anyone signed in with it is locked out. Use this when terminating an agreement. Share the new code only if the site should keep access."
          confirmLabel="Yes, new code"
          saving={saving}
          onConfirm={regenerate}
          onClose={() => setConfirmRegen(null)}
        />
      )}
      {confirmDeleteSite && (
        <Confirm
          testId="locker-del-site-confirm"
          title={`Remove ${confirmDeleteSite.name}?`}
          body="The site is deactivated and its code stops working immediately. All clients and entry history are retained in the database."
          confirmLabel="Yes, remove site"
          saving={saving}
          onConfirm={deleteSite}
          onClose={() => setConfirmDeleteSite(null)}
        />
      )}
    </div>
  );
}

// ---------- shared bits ----------

function Modal({ title, onClose, children, testId }) {
  // Restore the exact page scroll when the modal closes — iOS Safari scrolls
  // the page behind fixed overlays, which otherwise dumps you at the bottom.
  const savedScroll = useRef(0);
  useEffect(() => {
    savedScroll.current = window.scrollY;
    return () => window.scrollTo(0, savedScroll.current);
  }, []);
  return (
    <div data-testid={testId} onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 20, maxWidth: 460, width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>{title}</div>
          <button data-testid={`${testId}-close`} onClick={onClose}
            style={{ marginLeft: "auto", background: "none", border: "none", fontSize: 18, cursor: "pointer", color: MUTED }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Confirm({ title, body, confirmLabel, onConfirm, onClose, saving, testId }) {
  return (
    <Modal title={title} onClose={onClose} testId={testId}>
      <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.5, marginBottom: 16 }}>{body}</div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button data-testid={`${testId}-cancel`} style={btnGhost} onClick={onClose}>Cancel</button>
        <button data-testid={`${testId}-ok`} style={{ ...btn(RED), opacity: saving ? 0.7 : 1 }} onClick={onConfirm} disabled={saving}>
          {saving ? "Working…" : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

// Per-site cash float: HQ tops the site up, client cash payments deduct from
// it, and the Remaining tile stamps a daily reconciliation (one per date).
function SiteCashDialog({ site, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [amt, setAmt] = useState("");
  const [date, setDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [staffId, setStaffId] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadBalance = useCallback(async (sid) => {
    const { data } = await lapi.get(`/admin/sites/${site.id}/cash-balance`, { params: sid ? { staff_id: sid } : {} });
    setData(data);
  }, [site.id]);
  useEffect(() => { loadBalance(staffId).catch((e) => toast.error(e?.response?.data?.detail || "Load failed")); }, [loadBalance, staffId]);
  useEffect(() => {
    lapi.get("/admin/staff", { params: { site_id: site.id } })
      .then(({ data }) => setStaffList((data?.staff || []).filter((s) => s.active !== false)))
      .catch(() => {});
  }, [site.id]);

  const addTopup = async () => {
    const amount = Math.round((Number(amt) || 0) * 100) / 100;
    if (!(amount > 0)) { toast.error("Enter an amount"); return; }
    if (!date) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await lapi.post(`/admin/sites/${site.id}/cash-topups`, { amount, date, staff_id: staffId });
      toast.success(`Top-up added — ${money(amount)}`);
      setAmt("");
      await loadBalance(staffId); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const delTopup = async (t) => {
    if (!window.confirm(`Remove the ${money(t.amount)} top-up from ${fmtD(t.date)}?`)) return;
    setBusy(true);
    try {
      await lapi.delete(`/admin/cash-topups/${t.id}`);
      toast.success("Top-up removed");
      await loadBalance(staffId); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const tile = { flex: 1, minWidth: 0, background: "#FAF9F6", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "8px 4px", textAlign: "center" };
  const tileLabel = { fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".06em" };
  return (
    <Modal title={`Cash — ${site.name}`} onClose={onClose} testId="locker-cash-dialog">
      {!data ? (
        <div style={{ fontSize: 13, color: MUTED }} data-testid="locker-cash-loading">Loading…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={tile}><div style={tileLabel}>Cash in</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginTop: 2 }} data-testid="locker-cash-total-in">{money(data.topups_total)}</div></div>
            <div style={tile}><div style={tileLabel}>Given</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginTop: 2 }} data-testid="locker-cash-total-given">{money(data.given_total)}</div></div>
            <div style={tile}><div style={tileLabel}>Remaining</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: data.remaining < 0 ? RED : GREEN, marginTop: 2 }} data-testid="locker-cash-dlg-remaining">{money(data.remaining)}</div></div>
          </div>

          <div style={{ ...label, paddingLeft: 0, marginTop: 14 }}>Top ups</div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "nowrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={label}>Amount $</label>
              <input data-testid="locker-cash-topup-amount" type="number" min="0" step="0.01" inputMode="decimal" style={input} value={amt} onChange={(e) => setAmt(e.target.value)} />
            </div>
            <div style={{ flex: 1.3, minWidth: 0 }}>
              <label style={label}>Date</label>
              <input data-testid="locker-cash-topup-date" type="date" style={input} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <button type="button" data-testid="locker-cash-topup-add" onClick={addTopup} disabled={busy}
              style={{ ...btn(GREEN), height: 38, padding: "0 14px", flexShrink: 0, opacity: busy ? 0.7 : 1 }}>Add</button>
          </div>
          {staffList.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <label style={label}>Received by</label>
              <select data-testid="locker-cash-topup-staff" style={input} value={staffId} onChange={(e) => setStaffId(e.target.value)}>
                <option value="">Site float (not a specific staff member)</option>
                {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {(data.topups || []).length === 0 ? (
            <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }} data-testid="locker-cash-topups-empty">No top-ups yet.</div>
          ) : (
            <div style={{ marginTop: 6 }} data-testid="locker-cash-topups">
              {data.topups.map((t) => (
                <div key={t.id} data-testid={`locker-cash-topup-${t.id}`}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{fmtD(t.date)}</b> · {money(t.amount)}{t.staff_name ? ` · ${t.staff_name}` : ""}{t.note ? ` · ${t.note}` : ""}
                    <div style={{ fontSize: 10, color: MUTED }}>Added {fmtDT(t.created_at)}{t.created_by ? ` by ${t.created_by}` : ""}</div>
                  </div>
                  <button type="button" data-testid={`locker-cash-topup-del-${t.id}`} onClick={() => delTopup(t)}
                    style={{ ...btnGhost, padding: "5px 9px", fontSize: 11, color: RED, borderColor: "#E9BFB1" }}>Undo</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function SiteModal({ form, setForm, saving, onSave }) {
  const d = form.data;
  const set = (k) => (e) => setForm({ ...form, data: { ...d, [k]: e.target.value } });
  return (
    <Modal title={form.mode === "add" ? "Add locker site" : "Edit locker site"} onClose={() => setForm(null)} testId="locker-site-modal">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div style={{ gridColumn: "1 / -1" }}><label style={label}>Site name *</label>
          <input data-testid="locker-site-name" style={input} value={d.name} onChange={set("name")} placeholder="e.g. Queen St Community Centre" /></div>
        <div><label style={label}>Contact name</label>
          <input data-testid="locker-site-contact" style={input} value={d.contact_name} onChange={set("contact_name")} /></div>
        <div><label style={label}>Contact phone</label>
          <input data-testid="locker-site-phone" style={input} value={d.contact_phone} placeholder="(416) 555-0100"
            onChange={(e) => setForm({ ...form, data: { ...d, contact_phone: fmtPhone(e.target.value) } })} /></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={label}>Email</label>
          <input data-testid="locker-site-email" style={input} value={d.contact_email} onChange={set("contact_email")} /></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={label}>Status</label>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" data-testid="locker-site-active" onClick={() => setForm({ ...form, data: { ...d, enabled: true } })}
              style={{ flex: 1, height: 38, borderRadius: 8, border: `1.5px solid ${d.enabled !== false ? GREEN : BORDER}`, background: d.enabled !== false ? "#E7F2EC" : "#fff", color: d.enabled !== false ? GREEN : MUTED, fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Active</button>
            <button type="button" data-testid="locker-site-inactive" onClick={() => setForm({ ...form, data: { ...d, enabled: false } })}
              style={{ flex: 1, height: 38, borderRadius: 8, border: `1.5px solid ${d.enabled === false ? RED : BORDER}`, background: d.enabled === false ? "#FBEAE5" : "#fff", color: d.enabled === false ? RED : MUTED, fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Inactive</button>
          </div>
          {d.enabled === false && (
            <div style={{ fontSize: 11, color: RED, marginTop: 4 }} data-testid="locker-site-inactive-warn">The site code stops working until you set it back to Active.</div>
          )}
        </div>
        <div style={{ gridColumn: "1 / -1" }}><label style={label}>Street</label>
          <input data-testid="locker-site-street" style={input} value={d.street} onChange={set("street")} /></div>
        <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 52px 104px", gap: 8 }}>
          <div><label style={label}>City</label>
            <input data-testid="locker-site-city" style={input} value={d.city} onChange={set("city")} /></div>
          <div><label style={label}>Prov</label>
            <input data-testid="locker-site-province" style={input} value={d.province} maxLength={2}
              onChange={(e) => setForm({ ...form, data: { ...d, province: e.target.value.toUpperCase() } })} /></div>
          <div><label style={label}>Postal</label>
            <input data-testid="locker-site-postal" style={input} value={d.postal} maxLength={7} placeholder="A1A 1A1"
              onChange={(e) => setForm({ ...form, data: { ...d, postal: fmtPostal(e.target.value) } })} /></div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
        <button style={btnGhost} onClick={() => setForm(null)}>Cancel</button>
        <button data-testid="locker-site-save" style={{ ...btn(NAVY), opacity: saving ? 0.7 : 1 }} onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : form.mode === "add" ? "Add site" : "Save changes"}
        </button>
      </div>
    </Modal>
  );
}

function ClientModal({ form, setForm, saving, freeCompartments, board, fileRef, pickPhoto, onSave, onArchive, onRestrict, onRestore, photoOf, weeks, cashWeeks, onUndo, medications, onManageMeds, locations, onManageLocations, statuses, onManageStatuses, staffList = [] }) {
  const d = form.data;
  const [pasteOpen, setPasteOpen] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  // Paste a pharmacy label → pre-fill first name, last name, DOB.
  const fillFromLabel = (text) => {
    const parsed = parsePatientPaste(text);
    const { first_name, last_name } = splitName(parsed.patient_name || "");
    if (!first_name && !parsed.dob) {
      setForm((f) => ({ ...f, error: "Couldn't find a name or DOB in that label — check the format and try again" }));
      return;
    }
    setForm((f) => ({
      ...f,
      error: "",
      data: {
        ...f.data,
        first_name: first_name || f.data.first_name,
        last_name: last_name || f.data.last_name,
        dob: parsed.dob || f.data.dob,
      },
    }));
    toast.success("Label applied — name and DOB filled in");
  };
  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      fillFromLabel(text);
    } catch {
      setPasteOpen(true); // iOS Safari may block clipboard reads — manual box
    }
  };
  const set = (k) => (e) => setForm({ ...form, data: { ...d, [k]: e.target.value } });
  const isAdd = form.mode === "add";
  const current = !isAdd && board?.find((c) => c.occupied && c.client.id === form.id);
  const photoSrc = d.photo_data_url || (!isAdd && d.has_photo ? photoOf(form.id) : "");
  // Only free lockers (plus the client's current one) can be picked — taken
  // lockers never appear, so a client can only be moved somewhere available.
  // "No locker" is always offered: a site can have more clients than lockers.
  const compartmentOptions = isAdd
    ? freeCompartments
    : [Number(d.compartment), ...freeCompartments]
        .filter((v, i, a) => Number.isFinite(v) && v && a.indexOf(v) === i)
        .sort((a, b) => a - b);
  return (
    <Modal title={isAdd ? (d.compartment ? `Assign locker ${d.compartment}` : "Add client") : `${d.first_name} ${d.last_name}${d.compartment ? ` — locker ${d.compartment}` : ""}`} onClose={() => setForm(null)} testId="locker-client-form">
      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-start" }}>
        <button type="button" data-testid="locker-paste-label" onClick={pasteFromClipboard}
          style={{ ...btnGhost, flex: 1, minWidth: 0, height: 34, fontSize: 12, padding: "0 8px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, whiteSpace: "nowrap" }}>
          <ClipboardPaste style={{ width: 12, height: 12, flexShrink: 0 }} strokeWidth={2} />
          Paste label — auto-fill
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <select data-testid="locker-client-status" style={{ ...input, height: 34, fontSize: 12 }}
            value={d.client_status || "Active"} onChange={set("client_status")}>
            {(statuses || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <div style={{ marginTop: 2, textAlign: "right" }}>
            <button type="button" data-testid="locker-manage-statuses" onClick={onManageStatuses}
              style={{ background: "none", border: 0, padding: 0, color: NAVY, fontSize: 10, fontWeight: 700, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
              Edit dispositions
            </button>
          </div>
        </div>
      </div>
      {/* Adherence staff assignment — the person paid $50/week for this client */}
      <div style={{ marginBottom: 10 }}>
        <span style={label}>Adherence staff</span>
        <select data-testid="locker-client-staff" style={{ ...input, height: 34, fontSize: 12 }}
          value={d.staff_id || ""} onChange={set("staff_id")}>
          <option value="">— None —</option>
          {(staffList || []).filter((s) => s.active !== false).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>
      {pasteOpen && (
        <textarea
          data-testid="locker-paste-box"
          placeholder="Paste the label text here — name and DOB fill in automatically"
          onChange={(e) => fillFromLabel(e.target.value)}
          style={{ ...input, width: "100%", height: 64, padding: 8, marginBottom: 10, resize: "vertical", textTransform: "none", letterSpacing: 0 }}
        />
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><label style={label}>First name *</label>
          <input data-testid="locker-client-first" style={input} value={d.first_name} onChange={set("first_name")} /></div>
        <div><label style={label}>Last name *</label>
          <input data-testid="locker-client-last" style={input} value={d.last_name} onChange={set("last_name")} /></div>
        <div><label style={label}>Date of birth *</label>
          <input data-testid="locker-client-dob" type="date" style={input} value={d.dob} onChange={set("dob")} /></div>
        <div><label style={label}>Total tablets</label>
          <input data-testid="locker-client-tablets" type="number" min="1" max="365" style={input} value={d.total_tablets} onChange={set("total_tablets")} /></div>
        <div><label style={label}>Qualified cash $</label>
          <input data-testid="locker-client-cash" type="number" min="0" step="0.01" placeholder="0.00" style={input} value={d.total_cash} onChange={set("total_cash")} /></div>
        <div><label style={label}>Locker # <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
          <select data-testid="locker-client-compartment" style={input} value={d.compartment} onChange={set("compartment")}>
            <option value="">— No locker —</option>
            {compartmentOptions.map((n) => <option key={n} value={n}>{n}</option>)}
          </select></div>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap" }}>
            <label style={label}>ID photo</label>
            {photoSrc && (
              <button type="button" data-testid="locker-client-photo-replace" onClick={() => fileRef.current?.click()}
                style={{ background: "none", border: "none", padding: 0, marginBottom: 4, fontSize: 11, fontWeight: 700, color: NAVY, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
                New Photo
              </button>
            )}
          </div>
          <input ref={fileRef} data-testid="locker-client-photo" type="file" accept="image/*" style={{ display: "none" }}
            onChange={(e) => pickPhoto(e.target.files?.[0])} />
          <button data-testid="locker-client-photo-btn" style={{ ...btnGhost, width: "100%", height: 38, padding: "0 10px", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
            onClick={() => (photoSrc ? setViewPhoto(true) : fileRef.current?.click())}>
            {photoSrc ? "View photo" : "Upload / take photo"}
          </button>
        </div>
        {/* Medication — same shared dropdown the consultations module uses */}
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap" }}>
            <label style={label}>Medication</label>
            <button type="button" data-testid="locker-manage-meds" onClick={onManageMeds}
              style={{ background: "none", border: 0, padding: 0, marginBottom: 4, fontSize: 11, fontWeight: 700, color: NAVY, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
              Manage
            </button>
          </div>
          <select data-testid="locker-client-medication" style={input} value={d.medication || ""} onChange={set("medication")}>
            <option value="">— Please select —</option>
            {(medications || []).map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
        </div>
        {/* Medication Location — locker-managed list (Shelter / Outreach / Pharmacy…) */}
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap" }}>
            <label style={label}>Medication Location</label>
            <button type="button" data-testid="locker-manage-locations" onClick={onManageLocations}
              style={{ background: "none", border: 0, padding: 0, marginBottom: 4, fontSize: 11, fontWeight: 700, color: NAVY, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
              Manage
            </button>
          </div>
          <select data-testid="locker-client-location" style={input} value={d.location || ""} onChange={set("location")}>
            <option value="">— Please select —</option>
            {(locations || []).map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
          </select>
        </div>
        {/* Consultation status — partners set this in the portal; admins can
            change it here too (cash can't be recorded until it's Yes). */}
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={label}>Consultation completed</label>
          <select data-testid="locker-client-consultation" style={input} value={d.consultation || "no"} onChange={set("consultation")}>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
        {/* Notes — collapsible; saved with the client and shared with the
            locker site, where the partner can also update them. The Yes/No
            shows at a glance whether any of the three fields has content. */}
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
            <div role="button" tabIndex={0} data-testid="locker-notes-toggle" onClick={() => setNotesOpen((v) => !v)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "8px 12px", background: "#FAF9F6", cursor: "pointer", boxSizing: "border-box" }}>
              <span style={{ ...label, margin: 0, padding: 0 }}>Notes</span>
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {!notesOpen && (
                  <span data-testid="locker-notes-status" style={{ fontSize: 11, fontWeight: 800, color: ((d.phone || "").trim() || (d.notes || "").trim() || (d.notes_additional || "").trim()) ? GREEN : "#9AA0A6" }}>
                    {((d.phone || "").trim() || (d.notes || "").trim() || (d.notes_additional || "").trim()) ? "Yes" : "No"}
                  </span>
                )}
                <span style={{ fontSize: 11, fontWeight: 800, color: NAVY }}>
                  {notesOpen ? "▾ Hide" : `▸ ${((d.phone || "").trim() || (d.notes || "").trim() || (d.notes_additional || "").trim()) ? "View" : "Add"}`}
                </span>
              </span>
            </div>
            {notesOpen && (
              <div style={{ padding: "10px 12px 12px", borderTop: `1px solid ${BORDER}` }}>
                <span style={label}>Telephone</span>
                <input
                  data-testid="locker-client-phone"
                  value={d.phone || ""}
                  onChange={(e) => setForm({ ...form, data: { ...d, phone: fmtPhone(e.target.value) } })}
                  placeholder="(555) 555-9999"
                  inputMode="tel"
                  style={{ ...input, height: 34, fontSize: 13 }}
                />
                <span style={{ ...label, marginTop: 10 }}>Address / Contact</span>
                <AutoArea
                  data-testid="locker-client-notes"
                  value={d.notes || ""}
                  onChange={set("notes")}
                  placeholder="Address, shelter, contact person…"
                  style={{ ...input, width: "100%", padding: "8px 10px", fontSize: 12, textTransform: "none", letterSpacing: 0 }}
                />
                <span style={{ ...label, marginTop: 10 }}>Additional information</span>
                <AutoArea
                  data-testid="locker-client-notes-additional"
                  value={d.notes_additional || ""}
                  onChange={set("notes_additional")}
                  placeholder="Dated instructions, preferences…"
                  style={{ ...input, width: "100%", padding: "8px 10px", fontSize: 12, textTransform: "none", letterSpacing: 0 }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {current && (
        <div data-testid="locker-client-progress" style={{ fontSize: 11, color: MUTED, marginTop: 12 }}>
          {current.client.tablets_done} of {current.client.total_tablets} tablets given
          {current.client.complete ? " · treatment complete" : ""}
          {current.client.total_cash > 0 && (
            <span data-testid="locker-client-cash-progress">
              {" · "}{money(current.client.cash_given)} of {money(current.client.total_cash)} cash given
              {current.client.cash_complete ? " · fully paid" : ""}
            </span>
          )}
        </div>
      )}
      {!isAdd && (
        <div style={{ marginTop: 10 }} data-testid="locker-client-weeks">
          <label style={{ ...label, paddingLeft: 0 }}>Dispenses</label>
          {(weeks || []).length === 0 ? (
            <div style={{ fontSize: 12, color: MUTED }} data-testid="locker-weeks-empty">No entries yet.</div>
          ) : (weeks || []).map((p) => (
            <div key={p.id} data-testid={`locker-client-entry-${p.id}`}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{fmtD(p.service_date)}</b> · {p.tablets} tablets
                <div style={{ fontSize: 10, color: MUTED }}>Recorded {fmtDT(p.submitted_at)}</div>
              </div>
              {p.status === "submitted" && (
                <button data-testid={`locker-entry-undo-${p.id}`} style={{ ...btnGhost, padding: "5px 9px", fontSize: 11, color: RED, borderColor: "#E9BFB1" }} onClick={() => onUndo(p)}>Undo</button>
              )}
            </div>
          ))}
        </div>
      )}
      {!isAdd && (
        <div style={{ marginTop: 10 }} data-testid="locker-client-cash-entries">
          <label style={{ ...label, paddingLeft: 0 }}>Cash payments</label>
          {(cashWeeks || []).length === 0 ? (
            <div style={{ fontSize: 12, color: MUTED }} data-testid="locker-cash-entries-empty">No cash entries yet.</div>
          ) : (cashWeeks || []).map((p) => (
            <div key={p.id} data-testid={`locker-client-cash-entry-${p.id}`}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", borderTop: `1px solid ${BORDER}`, fontSize: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{fmtD(p.service_date)}</b> · {money(p.amount)} cash
                <div style={{ fontSize: 10, color: MUTED }}>Recorded {fmtDT(p.submitted_at)}</div>
              </div>
              {p.status === "submitted" && (
                <button data-testid={`locker-cash-undo-${p.id}`} style={{ ...btnGhost, padding: "5px 9px", fontSize: 11, color: RED, borderColor: "#E9BFB1" }} onClick={() => onUndo({ ...p, kind: "cash" })}>Undo</button>
              )}
            </div>
          ))}
        </div>
      )}
      {!isAdd && current?.client?.hold && (
        <div data-testid="locker-hold-banner" style={{ marginTop: 12, background: "#FBEAE5", border: `1px solid ${RED}`, color: RED, borderRadius: 8, padding: "9px 12px", fontSize: 12, fontWeight: 700 }}>
          ACCESS RESTRICTED — the site sees "Do not give medication — call HepCURE".
          {current.client.hold_reason ? ` Reason: ${current.client.hold_reason}` : ""}
        </div>
      )}
      {form.error && <div data-testid="locker-client-error" style={{ marginTop: 10, background: "#FBEAE5", border: "1px solid #E9BFB1", color: RED, borderRadius: 8, padding: "8px 10px", fontSize: 12, fontWeight: 600 }}>{form.error}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 14 }}>
        {!isAdd ? (
          <div style={{ display: "flex", gap: 6 }}>
            <button data-testid="locker-client-delete" style={{ ...btnGhost, color: RED, borderColor: "#E9BFB1" }}
              onClick={() => onArchive(current.client, current.n)}>Remove</button>
            {current?.client?.hold ? (
              <button data-testid="locker-client-unhold" style={{ ...btnGhost, color: GREEN, borderColor: GREEN, fontWeight: 700 }}
                onClick={() => onRestore(current.client)}>Restore</button>
            ) : (
              <button data-testid="locker-client-hold" style={{ ...btnGhost, color: "#B7791F", borderColor: "#E3C98F", fontWeight: 700 }}
                onClick={() => onRestrict(current.client)}>Revoke</button>
            )}
          </div>
        ) : <span />}
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="locker-client-save" style={{ ...btn(NAVY), opacity: saving ? 0.7 : 1 }} onClick={onSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button style={btnGhost} onClick={() => setForm(null)}>Cancel</button>
        </div>
      </div>
      {viewPhoto && photoSrc && (
        <div data-testid="locker-photo-lightbox" onClick={() => setViewPhoto(false)}
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", minHeight: "100vh", background: "rgba(0,0,0,.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box", zIndex: 80 }}>
          <button data-testid="locker-photo-lightbox-close" onClick={() => setViewPhoto(false)} aria-label="Close photo"
            style={{ position: "absolute", top: 14, right: 14, width: 42, height: 42, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.16)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X style={{ width: 20, height: 20 }} strokeWidth={2.5} />
          </button>
          <img src={photoSrc} alt="Client ID — full view" data-testid="locker-photo-lightbox-img" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }} />
        </div>
      )}
    </Modal>
  );
}

// Manage the shared medication list (same list the consultations module uses).
function MedsModal({ medications, onChanged, onClose }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setRows((medications || []).map((m) => ({ ...m, _draft: m.name }))); }, [medications]);

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.name) return;
    setBusy(true);
    try {
      await consultApi.patch(`/medications/${r.id}`, { name: r._draft.trim() });
      toast.success("Medication renamed");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    finally { setBusy(false); }
  };
  const deleteRow = async (r) => {
    if (!window.confirm(`Delete medication "${r.name}"?`)) return;
    setBusy(true);
    try {
      await consultApi.delete(`/medications/${r.id}`);
      toast.success("Medication deleted");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };
  const addRow = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await consultApi.post("/medications", { name });
      setNewName("");
      toast.success("Medication added");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Manage medications" onClose={onClose} testId="locker-meds">
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>Add, rename or delete. Shared with consultations.</div>
      {rows.length === 0 && (
        <div style={{ fontSize: 12, color: MUTED, padding: "6px 0" }}>No medications yet — add one below.</div>
      )}
      {rows.map((r, idx) => (
        <div key={r.id} data-testid={`locker-med-row-${r.id}`}
          style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 10px", border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 8 }}>
          <input value={r._draft}
            onChange={(e) => setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, _draft: e.target.value } : x)))}
            onBlur={() => saveRow(r)}
            data-testid={`locker-med-input-${r.id}`}
            style={{ flex: 1, border: 0, padding: 0, background: "transparent", fontSize: 13, color: TEXT, outline: "none", fontFamily: "inherit" }} />
          {r._draft.trim() && r._draft.trim() !== r.name && (
            <button type="button" onClick={() => saveRow(r)} data-testid={`locker-med-save-${r.id}`} title="Save"
              style={{ background: "none", border: "none", cursor: "pointer", color: GREEN, padding: 4 }}>
              <Check style={{ width: 15, height: 15 }} strokeWidth={2.5} />
            </button>
          )}
          <button type="button" onClick={() => deleteRow(r)} data-testid={`locker-med-delete-${r.id}`} title="Delete"
            style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: 4 }}>
            <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
          </button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <input data-testid="locker-new-med-input" value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
          placeholder="Add new medication…" style={{ ...input, flex: 1 }} />
        <button type="button" data-testid="locker-new-med-add" onClick={addRow} disabled={!newName.trim() || busy}
          style={{ ...btn(NAVY), opacity: !newName.trim() || busy ? 0.6 : 1 }}>Add</button>
      </div>
    </Modal>
  );
}

// Manage the medication location list (Shelter / Outreach Team / Pharmacy by
// default). Same add / rename / delete pattern as medications, plus a site
// picker that shows how many active clients are at each location per site.
function LocationsModal({ sites, currentSiteId, locations, onChanged, onClose }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState({});
  const [siteId, setSiteId] = useState(currentSiteId || "");

  useEffect(() => { setRows((locations || []).map((l) => ({ ...l, _draft: l.name }))); }, [locations]);

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await lapi.get("/admin/locations/counts");
      setCounts(data?.counts || {});
    } catch { /* keep the counts we have */ }
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const siteCounts = counts[siteId] || {};
  const countFor = (name) => {
    const n = siteCounts[name] || 0;
    return `${n} client${n === 1 ? "" : "s"}`;
  };

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.name) return;
    setBusy(true);
    try {
      await lapi.patch(`/locations/${r.id}`, { name: r._draft.trim() });
      toast.success("Location renamed");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    finally { setBusy(false); }
  };
  const deleteRow = async (r) => {
    if (!window.confirm(`Delete location "${r.name}"?`)) return;
    setBusy(true);
    try {
      await lapi.delete(`/locations/${r.id}`);
      toast.success("Location deleted");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };
  const addRow = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await lapi.post("/locations", { name });
      setNewName("");
      toast.success("Location added");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Manage locations" onClose={onClose} testId="locker-locations">
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>Where the client's medication is kept. Add, rename or delete.</div>
      <label style={label}>Client count per location — pick a site</label>
      <select data-testid="locker-locs-site" value={siteId} onChange={(e) => setSiteId(e.target.value)}
        style={{ ...input, marginBottom: 10 }}>
        <option value="">— Pick a site —</option>
        {(sites || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      {rows.length === 0 && (
        <div style={{ fontSize: 12, color: MUTED, padding: "6px 0" }}>No locations yet — add one below.</div>
      )}
      {rows.map((r, idx) => (
        <div key={r.id} data-testid={`locker-loc-row-${r.id}`}
          style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 10px", border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 8 }}>
          <input value={r._draft}
            onChange={(e) => setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, _draft: e.target.value } : x)))}
            onBlur={() => saveRow(r)}
            data-testid={`locker-loc-input-${r.id}`}
            style={{ flex: 1, minWidth: 0, border: 0, padding: 0, background: "transparent", fontSize: 13, color: TEXT, outline: "none", fontFamily: "inherit" }} />
          {siteId && (
            <span data-testid={`locker-loc-count-${r.id}`}
              style={{ fontSize: 10, fontWeight: 800, color: NAVY, border: `1.5px solid ${NAVY}`, borderRadius: 99, padding: "2px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>
              {countFor(r.name)}
            </span>
          )}
          {r._draft.trim() && r._draft.trim() !== r.name && (
            <button type="button" onClick={() => saveRow(r)} data-testid={`locker-loc-save-${r.id}`} title="Save"
              style={{ background: "none", border: "none", cursor: "pointer", color: GREEN, padding: 4 }}>
              <Check style={{ width: 15, height: 15 }} strokeWidth={2.5} />
            </button>
          )}
          <button type="button" onClick={() => deleteRow(r)} data-testid={`locker-loc-delete-${r.id}`} title="Delete"
            style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: 4 }}>
            <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
          </button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <input data-testid="locker-new-loc-input" value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
          placeholder="Add new location…" style={{ ...input, flex: 1 }} />
        <button type="button" data-testid="locker-new-loc-add" onClick={addRow} disabled={!newName.trim() || busy}
          style={{ ...btn(NAVY), opacity: !newName.trim() || busy ? 0.6 : 1 }}>Add</button>
      </div>
    </Modal>
  );
}

// Manage the client status list (Active / Pending / Inactive by default) —
// same add / rename / delete pattern as medications and locations.
function StatusesModal({ statuses, onChanged, onClose }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setRows((statuses || []).map((s) => ({ ...s, _draft: s.name }))); }, [statuses]);

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.name) return;
    setBusy(true);
    try {
      await lapi.patch(`/statuses/${r.id}`, { name: r._draft.trim() });
      toast.success("Status renamed");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    finally { setBusy(false); }
  };
  const deleteRow = async (r) => {
    if (!window.confirm(`Delete status "${r.name}"?`)) return;
    setBusy(true);
    try {
      await lapi.delete(`/statuses/${r.id}`);
      toast.success("Status deleted");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };
  const addRow = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await lapi.post("/statuses", { name });
      setNewName("");
      toast.success("Status added");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Edit dispositions" onClose={onClose} testId="locker-statuses">
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>Shown on each locker above the client's name. Add, rename or delete.</div>
      {rows.length === 0 && (
        <div style={{ fontSize: 12, color: MUTED, padding: "6px 0" }}>No statuses yet — add one below.</div>
      )}
      {rows.map((r, idx) => (
        <div key={r.id} data-testid={`locker-status-row-${r.id}`}
          style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 10px", border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 8 }}>
          <input value={r._draft}
            onChange={(e) => setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, _draft: e.target.value } : x)))}
            onBlur={() => saveRow(r)}
            data-testid={`locker-status-input-${r.id}`}
            style={{ flex: 1, minWidth: 0, border: 0, padding: 0, background: "transparent", fontSize: 13, color: TEXT, outline: "none", fontFamily: "inherit" }} />
          {r._draft.trim() && r._draft.trim() !== r.name && (
            <button type="button" onClick={() => saveRow(r)} data-testid={`locker-status-save-${r.id}`} title="Save"
              style={{ background: "none", border: "none", cursor: "pointer", color: GREEN, padding: 4 }}>
              <Check style={{ width: 15, height: 15 }} strokeWidth={2.5} />
            </button>
          )}
          <button type="button" onClick={() => deleteRow(r)} data-testid={`locker-status-delete-${r.id}`} title="Delete"
            style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: 4 }}>
            <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
          </button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <input data-testid="locker-new-status-input" value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
          placeholder="Add new status…" style={{ ...input, flex: 1 }} />
        <button type="button" data-testid="locker-new-status-add" onClick={addRow} disabled={!newName.trim() || busy}
          style={{ ...btn(NAVY), opacity: !newName.trim() || busy ? 0.6 : 1 }}>Add</button>
      </div>
    </Modal>
  );
}
